import { useCallback, useState } from 'react';

import { Box, useMediaQuery, useTheme } from '@mui/material';

import type { GetUserQuery } from '@graphql/__generated__/graphql';
import { type DateRange } from '@modules/_shared/components/feed-v2/components/DateRangeSelect';
import {
  CONTENT_HEIGHT,
  TwoPaneLayout,
} from '@modules/_shared/components/TwoPaneLayout';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { OnboardingStatusCard } from '@modules/users/user-overview/components/OnboardingStatusCard';
import { UserCatchmentAreaCard } from '@modules/users/user-overview/components/UserCatchmentAreaCard';
import { UserContactCard } from '@modules/users/user-overview/components/UserContactCard';
import { UserMetadataCard } from '@modules/users/user-overview/components/UserMetadataCard';
import { UserModulesCard } from '@modules/users/user-overview/components/UserModulesCard';
import { UserNotificationsCard } from '@modules/users/user-overview/components/UserNotificationsCard';
import { UserOverviewCard } from '@modules/users/user-overview/components/UserOverviewCard';
import { UserSubscriptionCard } from '@modules/users/user-overview/components/UserSubscriptionCard';
import { UserTeamsCard } from '@modules/users/user-overview/components/UserTeamsCard';
import { useCanViewSubscriptions } from '@modules/users/user-overview/hooks/useCanViewSubscriptions';
import UserFeedV2 from '@modules/users/user-overview/UserFeedV2';

type UserOverviewProps = {
  user: GetUserQuery['users_by_pk'];
};

// Regular users (neither admin nor broker) have little activity, so default
// their feed to the full history instead of the "last week" window.
const getFeedInitialDateRange = (user: {
  is_admin?: boolean | null;
  is_broker?: boolean | null;
}): DateRange => (!user.is_admin && !user.is_broker ? 'all' : 'week');

export const UserOverview = ({ user }: UserOverviewProps) => {
  const theme = useTheme();
  const isMobileLayout = useMediaQuery(theme.breakpoints.down('md'));
  const [scrollParent, setScrollParent] = useState<HTMLElement | null>(null);
  const scrollRef = useCallback((node: HTMLElement | null) => {
    setScrollParent(node);
  }, []);
  const { me } = useAppData();
  const canViewSubscriptions = useCanViewSubscriptions(user?.id ?? '');

  if (!user) {
    return null;
  }

  const isAdmin = me?.is_admin ?? false;
  const hasAnyTeam = (user.teams_users?.length ?? 0) > 0;
  const showTeamsCard = hasAnyTeam || user.is_broker || isAdmin;
  const showModulesCard = user.is_broker || user.is_admin || isAdmin;
  const showNotificationsCard = user.is_broker || user.is_admin;

  const primaryPhone =
    (user.phone_numbers.find(phone => phone.primary) ?? user.phone_numbers[0])
      ?.number ?? null;

  const feedInitialDateRange = getFeedInitialDateRange(user);

  const cards = (
    <Box>
      <UserOverviewCard user={user} />
      <OnboardingStatusCard user={user} />
      <UserContactCard user={user} />
      {showTeamsCard && <UserTeamsCard user={user} />}
      {showModulesCard && <UserModulesCard user={user} />}
      {(user.is_broker || user.is_admin) && (
        <UserCatchmentAreaCard userId={user.id} />
      )}
      {showNotificationsCard && <UserNotificationsCard user={user} />}
      {canViewSubscriptions && <UserSubscriptionCard user={user} />}
      <UserMetadataCard user={user} />
    </Box>
  );

  if (isMobileLayout) {
    return (
      <Box
        ref={scrollRef}
        sx={{
          height: CONTENT_HEIGHT,
          overflowY: 'auto',
          py: 2,
        }}
      >
        {cards}
        <UserFeedV2
          // Remount per profile so the role-based initialDateRange is re-applied
          // when the drawer is reused for a different user.
          key={user.id}
          userId={user.id}
          phoneNumber={primaryPhone}
          scrollParent={scrollParent}
          initialDateRange={feedInitialDateRange}
        />
      </Box>
    );
  }

  return (
    <TwoPaneLayout
      leftPane={cards}
      rightPane={
        <UserFeedV2
          // Remount per profile so the role-based initialDateRange is re-applied
          // when the drawer is reused for a different user.
          key={user.id}
          userId={user.id}
          phoneNumber={primaryPhone}
          scrollParent={scrollParent}
          initialDateRange={feedInitialDateRange}
        />
      }
      rightPaneRef={scrollRef}
      containerSx={{
        alignItems: 'flex-start',
        maxWidth: 1400,
        mx: 'auto',
        width: '100%',
      }}
    />
  );
};
